<template>
    <main
        v-if="!$wait.is('loading-page')"
        class="m-content-pages m-content-pages-str"
    >
        <y-container v-if="optimizationHeroData">
            <y-hero
                class="m-content-pages__hero"
                :hero-data="optimizationHeroData"
            />
        </y-container>

        <div class="m-content-pages-str__finger-tips">
            <y-container>
                <y-benefit
                    has-title-and-subtitle
                    :content="fingerTips"
                />
            </y-container>
        </div>
        <div class="m-content-pages-str__benefits">
            <y-container>
                <y-pwa-types
                    :pwa-types="benefits"
                    :custom-btn="benefits.custom"
                />
            </y-container>
        </div>

        <div class="m-content-pages-str__features-slider">
            <y-container>
                <y-features-slider
                    :slider-data="discoverFeatures"
                    :slideshow-options="slideshowOptions"
                />
            </y-container>
        </div>

        <y-container>
            <y-pwa-video
                v-if="videoData"
                class="m-content-pages__video"
                :streaming-data="videoData"
                dont-show-content
            />
        </y-container>
        <y-container>
            <y-significance
                v-if="significanceData"
                class="m-content-pages__significance"
                :content="significanceData"
            />
            <y-our-client-say
                v-if="testimonialsContent"
                :content="testimonialsContent"
                :clients-say="testimonialsSlideshow"
                title-tag="span"
                different-title
                header-img
                class="m-content-pages__testimonial"
            />
            <y-banner
                v-if="bannerData"
                class="m-content-pages__banner"
                :content="bannerData"
            />
        </y-container>
    </main>
</template>

<script >
    import PageMixin from '@groot-nodes/mixins/Page.js';
    import YHero from '@modules/content-pages/components/Hero/Hero.vue';
    import YSignificance from '@modules/content-pages/components/Significance/Significance.vue';
    import YPwaVideo from '@modules/pwa/components/PwaVideo/Index.vue';
    import YOurClientSay from '@components/OurClientsSay/Index.vue';
    import YBanner from '@components/ForPagesComponents/Banner/Banner.vue';
    import YPwaTypes from '@modules/pwa/components/PwaTypes/index.vue';
    import YFeaturesSlider from '@modules/pwa/components/FeaturesSlider/Index.vue';
    import YBenefit from '@modules/why-quarkino/components/Benefit/Benefit.vue';

    export default {
        name: 'ContentOptimizationPage',

        components: {
            YBenefit,
            YFeaturesSlider,
            YBanner,
            YOurClientSay,
            YPwaVideo,
            YHero,
            YSignificance,
            YPwaTypes,
        },

        mixins: [PageMixin],

        /**
         *
         * @inheritDoc
         */
        data() {
            return {
                optimizationHeroData   : null,
                fingerTips             : null,
                significanceData       : null,
                optimizationIntegration: null,
                testimonialsSlideshow  : null,
                videoData              : null,
                benefits               : null,
                testimonialsContent    : null,
                bannerData             : null,
                discoverFeatures       : null,
                slideshowOptions       : {
                    breakpoints: {
                        1140: {
                            slidesPerView: 2.9,
                            spaceBetween : 30,
                        },

                        0: {
                            slidesPerView: 1.3,
                            spaceBetween : 20,
                        },
                    },
                },
            };
        },

        /**
         *
         * @inheritDoc
         */
        metaInfo() {
            return {
                title: this.optimizationHeroData?.seo_post_title,

                meta: [
                    {
                        vmid   : 'description',
                        name   : 'description',
                        content: this.optimizationHeroData?.seo_meta_description,
                    },
                ],
            };
        },

        /**
         *
         * @inheritDoc
         */
        methods: {
            /**
             * fetch hero data
             *
             * @returns {Promise}
             */
            fetch() {
                this.$wait.start('loading-page');
                const optimizationHero = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'content-structure-hero',
                    including: ['image', 'title', 'synopsis', 'header-image', 'custom', 'title2', 'albums', 'seo_canonical', 'seo_meta_description', 'seo_post_title'],
                    locale   : this.$i18n.locale,

                }).catch((error) => {
                    this.handleError(error);
                });

                const structureSolution = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'content-structure-solutions',
                    including: ['image', 'title', 'synopsis', 'custom', 'text', 'albums', 'header-image'],
                    locale   : this.$i18n.locale,

                }).catch((error) => {
                    this.handleError(error);
                });

                const structureAdvantages = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'content-structure-advantages',
                    including: ['image', 'title', 'synopsis', 'custom', 'title2', 'albums', 'header-image', 'video'],
                    locale   : this.$i18n.locale,

                }).catch((error) => {
                    this.handleError(error);
                });

                const structureIntegration = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'content-structure-integration',
                    including: ['image', 'title', 'text', 'synopsis', 'custom', 'title2', 'albums', 'header-image'],
                    locale   : this.$i18n.locale,

                }).catch((error) => {
                    this.handleError(error);
                });

                const testimonialSliderShow = this.$services.Content.postList({
                    type     : 'testimonials',
                    tagging  : 'content-structure',
                    including: ['title', 'title2', 'header_image', 'image', 'custom', 'synopsis'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                return Promise.all([
                    optimizationHero,
                    structureSolution,
                    structureIntegration,
                    testimonialSliderShow,
                    structureAdvantages,
                ])
                    .then((responses) => {
                        if (this.handleReq(responses, 0)) {
                            this.optimizationHeroData = responses[0].data.results;
                            this.fingerTips = {
                                title2  : responses[0].data.results.custom.field1,
                                subtitle: responses[0].data.results.custom.field2,
                                albums  : responses[0].data.results.albums,
                                custom  : {
                                    ...responses[0].data.results.custom,
                                    'button-link' : responses[0].data.results.custom.field4,
                                    'button-title': responses[0].data.results.custom.field3,
                                },
                                image: responses[0].data.results['header-image'],
                            };
                        }

                        if (this.handleReq(responses, 1)) {
                            this.benefits = {
                                title   : responses[1].data.results.title,
                                synopsis: responses[1].data.results.synopsis,
                                albums  : responses[1].data.results.albums,
                                custom  : responses[1].data.results.custom,
                            };
                        }

                        if (this.handleReq(responses, 2)) {
                            this.significanceData = responses[2].data.results;
                            this.testimonialsContent = {
                                custom      : responses[2].data.results.custom,
                                header_image: responses[2].data.results['header-image'],

                            };
                            this.bannerData = {
                                custom: {
                                    ...responses[2].data.results.custom,
                                    field1        : responses[2].data.results.custom.field2,
                                    'button-title': responses[2].data.results.custom.field3,
                                    'button-link' : responses[2].data.results.custom.field4,
                                },
                            };
                        }

                        if (this.handleReq(responses, 3)) {
                            this.testimonialsSlideshow = responses[3].data.results;
                        }

                        if (this.handleReq(responses, 4)) {
                            this.videoData = {
                                title : responses[4].data.results.custom.field1,
                                albums: responses[4].data.results.albums.filter((item) => item.group === 'video'),
                                image : responses[4].data.results.image,
                                custom: {
                                    'button-title': responses[4].data.results.custom.field2,
                                    'button-link' : responses[4].data.results.custom.field3,
                                },
                            };
                            this.discoverFeatures = {
                                title   : responses[4].data.results.title,
                                synopsis: responses[4].data.results.custom.description,
                                albums  : responses[4].data.results.albums.filter((item) => item.group === 'album'),
                                custom  : {
                                    'button-title': responses[4].data.results.custom.field3,
                                    'button-link' : responses[4].data.results.custom.field4,
                                },
                            };
                        }
                    })
                    .catch((error) => {
                        this.handleError(error);
                    })
                    .finally(() => {
                        this.$wait.end('loading-page');
                    });
            },
        },
    };

</script>

<style lang="scss" src="@/modules/content-pages/scss/content-pages.scss"/>
